import { reactive, ref, watch } from 'vue';
import type { ProfileRecord } from './profiles/registry';

/**
 * The open profile's card (name, picture, logo) for the views, kept in step with its record in the
 * device's registry (docs/TZ-PROFILES.md): edits here are written to the registry and mark the card
 * as waiting for a future sync.
 */
export interface ProfileCard {
  /** The profile's id (stable: a future account tells profiles apart by it). */
  id: string;
  firstName: string;
  lastName: string;
  /** Picture (data: URL), empty — initials. */
  avatar: string;
  /** Mark shown on the home header. Empty until the profile provides one. */
  logo: string;
  /** pending = edited on this device and not confirmed by a future sync. */
  sync: 'pending' | 'acked';
}

/** What a chat line shows for the person who wrote it. */
export interface ChatAuthor {
  id: string;
  name: string;
  avatar: string;
}

export const profileCard = reactive<ProfileCard>({ id: '', firstName: '', lastName: '', avatar: '', logo: '', sync: 'acked' });

/** True once the open profile has a PIN. */
export const pinSet = ref(false);

let binding = false;
let timer: ReturnType<typeof setTimeout> | null = null;

/** The profile was opened (or its record changed elsewhere in this page): show its card. */
export function bindProfileCard(p: ProfileRecord): void {
  binding = true;
  Object.assign(profileCard, { id: p.id, firstName: p.firstName, lastName: p.lastName, avatar: p.avatar, logo: p.logo, sync: p.sync.state });
  pinSet.value = !!p.pin;
  binding = false;
}

watch(
  () => [profileCard.firstName, profileCard.lastName, profileCard.avatar, profileCard.logo],
  () => {
    if (binding || !profileCard.id) return;
    profileCard.sync = 'pending';
    // typing a name writes once it pauses
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void save(), 300);
  },
);

/** Write the card now. The name field writes itself after a pause; the settings button does not wait. */
export function saveProfileCard(): Promise<void> {
  if (timer) clearTimeout(timer);
  return save();
}

async function save(): Promise<void> {
  timer = null;
  const firstName = profileCard.firstName.trim();
  // the name is required: an emptied field is not written (the old name stays in the registry)
  if (!firstName) return;
  const { updateProfile } = await import('./profiles/registry');
  const { profileState } = await import('./profiles/state');
  try {
    profileState.current = await updateProfile(profileCard.id, { firstName: firstName.slice(0, 80), lastName: profileCard.lastName.trim().slice(0, 80), avatar: profileCard.avatar, logo: profileCard.logo }, true);
  } catch {
    /* the registry is gone (another tab deleted the profile): that tab reloads this one */
  }
}

/** The name and photo stamped onto a message at the moment it is sent. */
export function currentAuthor(): ChatAuthor {
  const name = [profileCard.firstName, profileCard.lastName]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' ');
  return { id: profileCard.id, name, avatar: profileCard.avatar };
}

/**
 * Future cloud sync reads this when the card changed here.
 * Null means nothing is waiting.
 */
export function pendingProfileSync(): ProfileCard | null {
  return profileCard.sync === 'pending' ? { ...profileCard } : null;
}

/** A future sync calls this after the cloud copy matches this card. */
export function ackProfileSync(): void {
  if (profileCard.sync === 'acked' || !profileCard.id) return;
  profileCard.sync = 'acked';
  void import('./profiles/registry').then(async ({ getProfile, putProfile }) => {
    const p = await getProfile(profileCard.id);
    if (p) await putProfile({ ...p, sync: { ...p.sync, state: 'acked' } });
  });
}

/** Future sync replaces the card with the cloud copy. No-op until one is connected. */
export const profileSync: { pull(): Promise<ProfileCard | null> } = {
  pull: () => Promise.resolve(null),
};
